import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Cupo, EventoPublico, Ponente, RespuestaBoleto } from '@eventos-cien/shared';
import { Button } from '../../components/Button';
import { Callout } from '../../components/Callout';
import { MapaEmbed } from '../../components/MapaEmbed';
import { TextField } from '../../components/TextField';
import { VideoEmbed } from '../../components/VideoEmbed';
import { api, ApiError } from '../../lib/api';

interface Props {
  token: string;
  cupo: Cupo;
  asesorNombre: string;
  evento: EventoPublico;
  onRegistrado: (b: RespuestaBoleto) => void;
}

interface Paleta {
  primario: string;
  secundario: string;
  terciario: string;
}

/**
 * Colores de la landing calculados en JS (no via var(--evento-primario, fallback)):
 * esa variable CSS siempre esta definida a nivel :root (cae en navy/oro del shell de
 * la app), asi que su "fallback" de var() nunca se usaria. Aqui sí distinguimos
 * tema configurado vs. no configurado, con un default neutro propio de esta landing.
 */
const PALETA_DEFAULT: Paleta = { primario: '#1c1a19', secundario: '#c9a05c', terciario: '#3c3129' };

function paletaDe(evento: EventoPublico): Paleta {
  return {
    primario: evento.tema.primario || PALETA_DEFAULT.primario,
    secundario: evento.tema.secundario || PALETA_DEFAULT.secundario,
    terciario: evento.tema.terciario || PALETA_DEFAULT.terciario,
  };
}

export function Landing(props: Props) {
  const { evento } = props;
  const paleta = paletaDe(evento);

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-fondo">
      <Hero evento={evento} paleta={paleta} />
      <Ponentes ponentes={evento.ponentes} />
      <UbicacionRegistro {...props} paleta={paleta} />
      <Footer footer={evento.footer} paleta={paleta} />
    </div>
  );
}

function Hero({ evento, paleta }: { evento: EventoPublico; paleta: Paleta }) {
  return (
    <section
      className="w-full px-5 py-14 sm:px-8 md:py-20"
      style={{
        background: `linear-gradient(135deg, ${paleta.primario} 0%, ${paleta.terciario} 55%, ${paleta.secundario} 130%)`,
      }}
    >
      <div className="mx-auto flex max-w-5xl flex-col items-center gap-8 md:flex-row md:gap-12">
        {evento.heroImagenUrl && (
          <div className="w-full max-w-xs md:w-[45%] md:max-w-none">
            <img src={evento.heroImagenUrl} alt="" className="w-full rounded-2xl object-cover shadow-2xl" />
          </div>
        )}
        <div className={`w-full text-center ${evento.heroImagenUrl ? 'md:w-[55%]' : ''}`}>
          {evento.fechaTexto && (
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/60">
              {evento.fechaTexto}
              {evento.horaTexto && ` · ${evento.horaTexto}`}
            </span>
          )}
          <h1 className="mt-2 text-3xl font-bold leading-tight text-white sm:text-4xl md:text-5xl">
            {evento.nombre}
          </h1>
          {evento.lugarTexto && <p className="mt-3 text-white/70">{evento.lugarTexto}</p>}
          <a
            href="#registro"
            className="mt-7 inline-flex items-center justify-center rounded-full px-7 py-3.5 text-sm font-semibold text-white shadow-lg transition hover:brightness-110 active:scale-[0.98]"
            style={{ backgroundColor: paleta.secundario }}
          >
            Confirmar asistencia
          </a>
        </div>
      </div>
    </section>
  );
}

function Ponentes({ ponentes }: { ponentes: Ponente[] }) {
  const visibles = ponentes.filter((p) => p.nombre?.trim());
  if (visibles.length === 0) return null;

  return (
    <section className="w-full bg-[#f7f5f0] px-5 py-14 sm:px-8">
      <div className="mx-auto max-w-5xl">
        <h2 className="text-center text-2xl font-bold text-[#2b2622] sm:text-3xl">Ponentes del evento</h2>
        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
          {visibles.map((p, i) => (
            <div
              key={i}
              className="rounded-2xl border border-black/5 bg-white p-6 text-center shadow-[0_4px_16px_rgba(0,0,0,0.05)]"
            >
              {p.fotoUrl && (
                <div className="mx-auto h-32 w-32 overflow-hidden rounded-full bg-[#f0ece4] sm:h-36 sm:w-36">
                  <img src={p.fotoUrl} alt="" className="h-full w-full object-contain" />
                </div>
              )}
              <h3 className="mt-4 text-lg font-semibold text-[#2b2622]">{p.nombre}</h3>
              {/* min-h reserva 2 líneas siempre (con o sin subtítulo, corto o largo) para que la
                  biografía de las 3 tarjetas empiece exactamente a la misma altura. */}
              <p className="mt-0.5 min-h-10 text-sm font-medium text-[#8a6b45]">{p.subtitulo}</p>
              {p.descripcion && (
                <p className="mt-2 text-sm leading-relaxed text-[#2b2622]/70">{p.descripcion}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function UbicacionRegistro({ token, cupo, asesorNombre, evento, onRegistrado, paleta }: Props & { paleta: Paleta }) {
  return (
    <section
      className="w-full px-5 py-14 sm:px-8 md:py-20"
      style={{ background: `linear-gradient(160deg, #14120f 0%, ${paleta.primario} 100%)` }}
    >
      <div className="mx-auto max-w-5xl">
        <div className="text-center text-white md:text-left">
          <h2 className="text-2xl font-bold sm:text-3xl">{evento.lugarTexto || 'Ubicación'}</h2>
          {evento.direccionTexto && <p className="mt-2 text-white/80">{evento.direccionTexto}</p>}
          {evento.fechaTexto && (
            <p className="mt-2 text-white/70">
              {evento.fechaTexto}
              {evento.horaTexto && ` · ${evento.horaTexto}`}
            </p>
          )}
          {evento.mapsUrl && (
            <a
              href={evento.mapsUrl}
              target="_blank"
              rel="noopener"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-[#2b2622] shadow-lg transition hover:brightness-95 active:scale-[0.98]"
            >
              Abrir en Maps
            </a>
          )}
        </div>

        {(evento.videoUrl || evento.mapaEmbedUrl) && (
          <div className={`mt-8 grid gap-6 ${evento.videoUrl && evento.mapaEmbedUrl ? 'md:grid-cols-2' : ''}`}>
            {evento.mapaEmbedUrl && (
              <div>
                <MapaEmbed url={evento.mapaEmbedUrl} />
              </div>
            )}
            {evento.videoUrl && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-white/50">
                  Cómo llegar
                </p>
                <div className="mt-2">
                  <VideoEmbed url={evento.videoUrl} />
                </div>
              </div>
            )}
          </div>
        )}

        <div id="registro" className="mt-12 overflow-hidden rounded-2xl bg-white shadow-2xl">
          <div className="h-1.5 w-full" style={{ backgroundColor: paleta.secundario }} />
          <div className="p-8 sm:p-10 md:p-12">
            <div className="mx-auto max-w-xl">
              <RegistroForm
                token={token}
                cupo={cupo}
                asesorNombre={asesorNombre}
                evento={evento}
                onRegistrado={onRegistrado}
                paleta={paleta}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer({ footer, paleta }: { footer: EventoPublico['footer']; paleta: Paleta }) {
  const tieneContenido =
    footer.titulo || footer.descripcion || footer.facebookUrl || footer.instagramUrl || footer.detalles.length > 0;
  if (!tieneContenido) return null;

  return (
    <footer className="w-full px-5 py-14 sm:px-8" style={{ backgroundColor: paleta.terciario }}>
      <div className="mx-auto grid max-w-5xl gap-10 md:grid-cols-2">
        <div className="text-white">
          {footer.titulo && <h2 className="text-2xl font-bold sm:text-3xl">{footer.titulo}</h2>}
          {footer.descripcion && <p className="mt-3 text-white/70">{footer.descripcion}</p>}
          {(footer.facebookUrl || footer.instagramUrl) && (
            <div className="mt-6 flex gap-4">
              {footer.facebookUrl && (
                <a
                  href={footer.facebookUrl}
                  target="_blank"
                  rel="noopener"
                  aria-label="Facebook"
                  className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-[#2b2622] transition hover:brightness-95 active:scale-95"
                >
                  <IconFacebook />
                </a>
              )}
              {footer.instagramUrl && (
                <a
                  href={footer.instagramUrl}
                  target="_blank"
                  rel="noopener"
                  aria-label="Instagram"
                  className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-[#2b2622] transition hover:brightness-95 active:scale-95"
                >
                  <IconInstagram />
                </a>
              )}
            </div>
          )}
        </div>
        {footer.detalles.length > 0 && (
          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 self-start sm:grid-cols-2">
            {footer.detalles.map((d, i) => (
              <div key={i} className="flex flex-wrap gap-x-1.5 text-sm">
                <dt className="font-semibold text-white">{d.etiqueta}:</dt>
                <dd className="wrap-break-word text-white/80">{d.valor}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </footer>
  );
}

function IconFacebook() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M13.5 9H15.5V6H13.5C11.6 6 10 7.6 10 9.5V11H8.5V14H10V19H13V14H14.9L15.5 11H13V9.7C13 9.3 13.2 9 13.5 9Z" />
    </svg>
  );
}

function IconInstagram() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <rect x="3.5" y="3.5" width="17" height="17" rx="5" />
      <circle cx="12" cy="12" r="3.6" />
      <circle cx="17.2" cy="6.8" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}

function RegistroForm({ token, cupo, evento, onRegistrado, paleta }: Props & { paleta: Paleta }) {
  const [searchParams] = useSearchParams();
  const [nombre, setNombre] = useState('');
  const [llevaAcompanante, setLlevaAcompanante] = useState(true);
  const [acompanante, setAcompanante] = useState('');
  // Prellenado desde la liga (?telefono=...) cuando quien la manda ya conoce el numero.
  const [telefono, setTelefono] = useState(() => searchParams.get('telefono') ?? '');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setEnviando(true);
    try {
      const boleto = await api<RespuestaBoleto>(`/i/${encodeURIComponent(token)}/registro`, {
        method: 'POST',
        body: JSON.stringify({
          nombre,
          acompanante: cupo === 2 && llevaAcompanante ? acompanante : null,
          telefono,
        }),
      });
      onRegistrado(boleto);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No se pudo completar el registro');
      setEnviando(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2.5 text-center">
        <h2 className="text-2xl font-bold text-texto">Confirma tu asistencia</h2>
        <span className="mx-auto block h-1 w-12 rounded-full" style={{ backgroundColor: paleta.secundario }} />
      </div>

      {!evento.registroAbierto && (
        <Callout tono="alerta">El registro no está abierto en este momento.</Callout>
      )}

      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        <TextField
          label="Nombre completo"
          required
          value={nombre}
          autoComplete="name"
          autoCapitalize="words"
          onChange={(e) => setNombre(e.target.value)}
        />
        {cupo === 2 && (
          <div className="space-y-3">
            <span className="text-sm font-medium text-texto">¿Vas a llevar acompañante?</span>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                aria-pressed={llevaAcompanante}
                onClick={() => setLlevaAcompanante(true)}
                className={[
                  'rounded-lg border-2 px-4 py-3.5 text-base font-semibold transition',
                  llevaAcompanante ? '' : 'border-borde text-texto',
                ].join(' ')}
                style={
                  llevaAcompanante
                    ? { borderColor: paleta.secundario, backgroundColor: `${paleta.secundario}1a`, color: paleta.primario }
                    : undefined
                }
              >
                Sí, voy acompañado
              </button>
              <button
                type="button"
                aria-pressed={!llevaAcompanante}
                onClick={() => {
                  setLlevaAcompanante(false);
                  setAcompanante('');
                }}
                className={[
                  'rounded-lg border-2 px-4 py-3.5 text-base font-semibold transition',
                  !llevaAcompanante ? '' : 'border-borde text-texto',
                ].join(' ')}
                style={
                  !llevaAcompanante
                    ? { borderColor: paleta.secundario, backgroundColor: `${paleta.secundario}1a`, color: paleta.primario }
                    : undefined
                }
              >
                No, voy solo
              </button>
            </div>
            {llevaAcompanante && (
              <TextField
                label="Nombre completo de tu acompañante"
                required
                value={acompanante}
                autoCapitalize="words"
                onChange={(e) => setAcompanante(e.target.value)}
              />
            )}
          </div>
        )}
        {evento.telefonoRequerido && (
          <TextField
            label="Teléfono"
            type="tel"
            inputMode="tel"
            required
            value={telefono}
            autoComplete="tel"
            hint="Lo usamos como respaldo en la entrada"
            onChange={(e) => setTelefono(e.target.value)}
          />
        )}
        {error && <Callout tono="error">{error}</Callout>}
        <Button type="submit" bloque grande cargando={enviando} disabled={!evento.registroAbierto}>
          Confirmar asistencia
        </Button>
      </form>

      <p className="text-center text-xs text-texto/50">
        Esta invitación es personal y de un solo uso: al enviarla, esta misma liga se convierte en tu
        boleto.
      </p>
    </div>
  );
}
